// contexts/AnalyticsContext.jsx
// Charge et active GA4 selon le mode d'usage :
// - direct (app.vocaverb.com) : activation immediate (pas de gestion de consentement cote app pour
//   l'instant - point de vigilance RGPD, voir CLAUDE.md du projet).
// - embed (iframe WP) : activation seulement apres consentement relaye par WP via postMessage
//   (le listener cote WP qui emet ce message reste a construire separement, hors scope ici).

import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const WP_ORIGIN = 'https://vocaverb.com';

const AnalyticsContext = createContext(null);

function activerGtag(measurementId) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', measurementId);
}

export function AnalyticsProvider({ children }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { loading, isAdmin } = useAuth();
  const activeRef = useRef(false);

  const isEmbed = searchParams.get('embed') === 'true' || location.pathname.includes('/embed/');

  // Mode direct : activation immediate, une fois l'admin exclu confirme (fin du chargement auth)
  useEffect(() => {
    if (!GA_MEASUREMENT_ID || activeRef.current || isEmbed || loading || isAdmin) return;
    activerGtag(GA_MEASUREMENT_ID);
    activeRef.current = true;
  }, [isEmbed, loading, isAdmin]);

  // Mode embed : activation uniquement sur reception d'un consentement relaye par WP
  useEffect(() => {
    if (!GA_MEASUREMENT_ID || !isEmbed) return;

    function onMessage(event) {
      if (event.origin !== WP_ORIGIN) return;
      if (event.data?.type !== 'vv:consentement-analytics') return;
      if (event.data.granted && !activeRef.current && !isAdmin) {
        activerGtag(GA_MEASUREMENT_ID);
        activeRef.current = true;
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [isEmbed, isAdmin]);

  return <AnalyticsContext.Provider value={null}>{children}</AnalyticsContext.Provider>;
}

export function useAnalytics() {
  return useContext(AnalyticsContext);
}

export default AnalyticsContext;
